import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { adminApi } from "services/adminApi"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { UserStatusTypes } from "types"
import type { Viewer3DList, Viewer3DPublishableKey } from "../types"
import { deriveViewer3DListFields, patchViewer3DListRow } from "./viewer3DListCache"
import { removeViewer3DKey, upsertViewer3DKey, type Viewer3DKeysList } from "./viewer3DKeysCache"

export const VIEWER3D_PRODUCTS_KEY = ["viewer3d", "products"] as const
export const VIEWER3D_PRODUCT_KEY = (id: string) => ["viewer3d", "product", id] as const
export const VIEWER3D_KEYS_KEY = ["viewer3d", "publishable-keys"] as const
export const VIEWER3D_KEYS_QUERY_KEY = (merchantId: string) =>
  [...VIEWER3D_KEYS_KEY, merchantId] as const
export const VIEWER3D_DEMO_KEY_QUERY_KEY = ["viewer3d", "demo-key"] as const

const VIEWER3D_CATALOG_PAGE_SIZE = 100
const VIEWER3D_CATALOG_MAX_REQUESTS = 100

export const VIEWER3D_PRODUCTS_QUERY_KEY = (merchantId: string) =>
  [...VIEWER3D_PRODUCTS_KEY, merchantId] as const

const loadViewer3DProductCatalog = async (): Promise<Viewer3DList> => {
  const items: Viewer3DList["items"] = []
  const seenCursors = new Set<string>()
  let cursor: string | undefined

  for (let request = 0; request < VIEWER3D_CATALOG_MAX_REQUESTS; request += 1) {
    const result = await adminApi.listViewer3DProducts({
      cursor,
      limit: VIEWER3D_CATALOG_PAGE_SIZE,
    })
    items.push(...result.items)

    if (!result.nextCursor) return { items }
    if (seenCursors.has(result.nextCursor))
      throw new Error("The 3D product catalog returned a repeated pagination cursor.")

    seenCursors.add(result.nextCursor)
    cursor = result.nextCursor
  }

  throw new Error("The 3D product catalog exceeds the supported pagination depth.")
}

export const useViewer3DProductsQuery = () => {
  const userInfo = useAppSelector(selectUserInfo)
  const merchantId = userInfo?.merchantId ?? ""
  return useQuery({
    queryKey: VIEWER3D_PRODUCTS_QUERY_KEY(merchantId),
    queryFn: loadViewer3DProductCatalog,
    enabled: Boolean(merchantId),
    staleTime: 60_000,
    retry: false,
  })
}

export const useViewer3DProductQuery = (productId?: string) =>
  useQuery({
    queryKey: VIEWER3D_PRODUCT_KEY(productId ?? "none"),
    queryFn: () => adminApi.getViewer3DProduct(productId as string),
    enabled: Boolean(productId),
    retry: false,
  })

/**
 * scopeMerchantId: superadmin ?id=… browsing scope (same contract as the
 * try-on integrations page); merchants always resolve their own scope.
 */
export const useViewer3DKeysQuery = (scopeMerchantId?: string, enabled = true) => {
  const userInfo = useAppSelector(selectUserInfo)
  const merchantId = scopeMerchantId || (userInfo?.merchantId ?? "")
  return useQuery({
    queryKey: VIEWER3D_KEYS_QUERY_KEY(merchantId),
    queryFn: () => adminApi.listViewer3DPublishableKeys(scopeMerchantId),
    enabled: enabled && Boolean(merchantId),
    retry: false,
  })
}

/**
 * Warms the integrations list from the workspace layout: the first GET rides a
 * cold per-function Lambda plus a full authorizer re-run, which QA read as
 * "домены подгружаются ~2 секунды" on the integration dialog. Prefetching at
 * login hides that behind the workspace load. Skipped for admins (their scope
 * comes from ?id=, unknowable here) and for non-ACTIVE users — a pre-activation
 * call can get an authorizer Deny cached against the token by API Gateway.
 */
export const usePrefetchViewer3DKeys = () => {
  const queryClient = useQueryClient()
  const userInfo = useAppSelector(selectUserInfo)
  const isAdmin = userInfo?.groups.some((group) => group.trim().toLowerCase() === "admin")
  const isActive = userInfo?.status === UserStatusTypes.ACTIVE
  const merchantId = userInfo?.merchantId ?? ""

  useEffect(() => {
    if (isAdmin || !isActive || !merchantId) return
    void queryClient.prefetchQuery({
      queryKey: VIEWER3D_KEYS_QUERY_KEY(merchantId),
      queryFn: () => adminApi.listViewer3DPublishableKeys(),
      staleTime: 60_000,
    })
  }, [queryClient, isAdmin, isActive, merchantId])
}

export const useDeleteViewer3DProductMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (productId: string) => adminApi.deleteViewer3DProduct(productId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VIEWER3D_PRODUCTS_KEY }),
  })
}

export const useToggleViewer3DPublishMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, publish }: { productId: string; publish: boolean }) =>
      publish
        ? adminApi.publishViewer3DProduct(productId)
        : adminApi.unpublishViewer3DProduct(productId),

    // Flip the row immediately. The Switch is fully controlled by `published`,
    // so without this it simply does not move until the request lands.
    onMutate: async ({ productId, publish }) => {
      await queryClient.cancelQueries({ queryKey: VIEWER3D_PRODUCTS_KEY })
      const snapshot = queryClient.getQueriesData<Viewer3DList>({
        queryKey: VIEWER3D_PRODUCTS_KEY,
      })
      queryClient.setQueriesData<Viewer3DList>({ queryKey: VIEWER3D_PRODUCTS_KEY }, (previous) =>
        patchViewer3DListRow(previous, productId, () => ({ published: publish }))
      )
      return { snapshot }
    },

    // Patch the cached row from the returned record instead of refetching: an
    // active refetch re-runs the whole cursor-paged catalog load and reorders
    // rows under the cursor, which is what made the list freeze and the wrong
    // toggle appear to react.
    onSuccess: (product) => {
      queryClient.setQueriesData<Viewer3DList>({ queryKey: VIEWER3D_PRODUCTS_KEY }, (previous) =>
        patchViewer3DListRow(previous, product.productId, () => ({
          published: product.published,
          enabled: product.enabled,
          ...deriveViewer3DListFields(product.variants),
        }))
      )
      queryClient.invalidateQueries({ queryKey: VIEWER3D_PRODUCTS_KEY, refetchType: "none" })
      queryClient.setQueryData(VIEWER3D_PRODUCT_KEY(product.productId), product)
    },

    onError: (_error, _variables, context) => {
      for (const [key, data] of context?.snapshot ?? []) queryClient.setQueryData(key, data)
      // publish() commits the record before it cleans up S3, so a late failure
      // can leave the server published while we roll the client back. Errors are
      // rare enough to pay for one authoritative refetch.
      queryClient.invalidateQueries({ queryKey: VIEWER3D_PRODUCTS_KEY })
    },
  })
}

/**
 * Same scope resolution as `useViewer3DKeysQuery`, so mutations patch exactly
 * the cache entry that query reads.
 */
const useViewer3DKeysScope = (scopeMerchantId?: string) => {
  const userInfo = useAppSelector(selectUserInfo)
  return scopeMerchantId || (userInfo?.merchantId ?? "")
}

/**
 * The mutation response IS the authoritative record (see `viewer3DKeysCache.ts`),
 * so write it into the cached list instead of refetching: the follow-up GET rode
 * a cold Lambda and left the saved dialog showing pre-save origins for ~2s.
 * `cancelQueries` first — an in-flight stale GET must not overwrite the fresher
 * mutation result. Precedent: `useToggleViewer3DPublishMutation` above.
 */
const writeViewer3DKeysCache = async (
  queryClient: ReturnType<typeof useQueryClient>,
  merchantId: string,
  write: (previous: Viewer3DKeysList | undefined) => Viewer3DKeysList | undefined
) => {
  await queryClient.cancelQueries({ queryKey: VIEWER3D_KEYS_KEY })
  queryClient.setQueryData<Viewer3DKeysList>(VIEWER3D_KEYS_QUERY_KEY(merchantId), write)
  queryClient.invalidateQueries({ queryKey: VIEWER3D_KEYS_KEY, refetchType: "none" })
}

export const useCreateViewer3DKeyMutation = (scopeMerchantId?: string) => {
  const queryClient = useQueryClient()
  const merchantId = useViewer3DKeysScope(scopeMerchantId)
  return useMutation({
    mutationFn: (payload: { label?: string | null; allowedOrigins: string[] }) =>
      adminApi.createViewer3DPublishableKey({ ...payload, merchantId: scopeMerchantId }),
    onSuccess: (key) =>
      writeViewer3DKeysCache(queryClient, merchantId, (previous) =>
        upsertViewer3DKey(previous, key)
      ),
  })
}

export const useRevokeViewer3DKeyMutation = (scopeMerchantId?: string) => {
  const queryClient = useQueryClient()
  const merchantId = useViewer3DKeysScope(scopeMerchantId)
  return useMutation({
    mutationFn: (keyHash: string) =>
      adminApi.revokeViewer3DPublishableKey(keyHash, scopeMerchantId),
    onSuccess: (_result, keyHash) =>
      writeViewer3DKeysCache(queryClient, merchantId, (previous) =>
        removeViewer3DKey(previous, keyHash)
      ),
  })
}

/** Rename an integration or change its Store URLs — the key never changes. */
export const useUpdateViewer3DKeyOriginsMutation = (scopeMerchantId?: string) => {
  const queryClient = useQueryClient()
  const merchantId = useViewer3DKeysScope(scopeMerchantId)
  return useMutation({
    mutationFn: ({
      keyHash,
      ...payload
    }: {
      keyHash: string
      allowedOrigins?: string[]
      label?: string
    }) =>
      adminApi.updateViewer3DPublishableKeyOrigins(keyHash, {
        ...payload,
        merchantId: scopeMerchantId,
      }),
    onSuccess: (key: Viewer3DPublishableKey) =>
      writeViewer3DKeysCache(queryClient, merchantId, (previous) =>
        upsertViewer3DKey(previous, key)
      ),
  })
}

/**
 * Resolves the merchant's reusable demo token, minting it on first use.
 * Cached for the session so a row of "View demo" buttons issues one request,
 * and mutation-shaped because the first call can create the key.
 */
export const useEnsureViewer3DDemoKeyMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const cached = queryClient.getQueryData<{ allowedOrigins: string[]; token: string }>(
        VIEWER3D_DEMO_KEY_QUERY_KEY
      )
      if (cached?.token) return cached

      const result = await adminApi.ensureViewer3DDemoKey()
      queryClient.setQueryData(VIEWER3D_DEMO_KEY_QUERY_KEY, result)
      return result
    },
  })
}
